import { useState } from 'react';
import PropTypes from 'prop-types';
import { Input, Divider, Form } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { getUsername } from '@vantiva-sa/homesight_humaui/Utilities/CommonUtilities';
import Contact from '../../Common/Contact';
import Loading from '../../Common/Loading';
import { useSelector } from 'react-redux';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';

const ContactSelectionForStaff = ({
  fetchingContacts = false,
  selectionContactList = [],
  onAddStaff = () => {},
  setSelectedStaff,
  selectedStaff,
  hubRecord,
  setSearchNameApplied,
  searchNameApplied,
  setSearchNameText,
  searchNameText,
  linkAccountChecked = false,
  formData,
  handleInputChange,
}) => {
  const trans = useSelector(translator);
  const [filteredContacts, setFilteredContacts] = useState(selectionContactList);

  // Check if the selected staff has a user account
  const staffHasUser = selectedStaff && selectedStaff.useruuid;

  const handleSearchApply = () => {
    setSelectedStaff(null); // Clear selected staff on new search
    if (!searchNameText?.trim()) {
      setFilteredContacts(selectionContactList);
      setSearchNameApplied(false);
      return;
    }

    const filtered = selectionContactList.filter((contact) =>
      getUsername(contact).toLowerCase().includes(searchNameText.toLowerCase())
    );
    setFilteredContacts(filtered);
    setSearchNameApplied(true);
  };

  const handleClearSearch = () => {
    setSearchNameText('');
    setFilteredContacts(selectionContactList);
    setSearchNameApplied(false);
  };

  const handleSelectContact = (contact) => {
    setSelectedStaff(contact);
    onAddStaff(contact);
    handleClearSearch();
  };

  const handleKeyDown = (e, item) => {
    if (e.key === 'Enter') {
      handleSelectContact(item);
    }
  };

  const handleChange = (e) => {
    setSearchNameText(e.target.value);
  };

  const handleInputEnter = (e) => {
    if (e.key === 'Enter') {
      handleSearchApply();
    }
  };

  return (
    <>
      {linkAccountChecked && (
        <div className="link-social-account">
          {trans('ADDSOCIALCONTACTTO')} {hubRecord?.firstname}
        </div>
      )}
      <div className="new-contact-selection-main-container">
        <div className="new-contact-search-container">
          <label className="select-a-staff-label">
            {linkAccountChecked ? trans('FINDANACCOUNT') : trans('SELECT_A_STAFF_U_MEMEBER')}
          </label>
          <div className="new-contact-search-input">
            <Input
              className="new-search-input"
              value={searchNameText}
              onChange={handleChange}
              onKeyDown={handleInputEnter}
              placeholder={linkAccountChecked ? trans('ENTER_ACCOUNT_L') : trans('SEARCH_BY_NAME')}
              suffix={searchNameApplied ? <CloseOutlined onClick={handleClearSearch} /> : null}
            />
          </div>
        </div>
        <div className="new-contact-list-container">
          {fetchingContacts && <Loading />}

          {!fetchingContacts && filteredContacts.length === 0 && (
            <div className="no-filtered-contacts-container">
              <span className="no-contact-available">{trans('NOCONTACTSFOUND')}</span>
            </div>
          )}

          {selectedStaff == null &&
            filteredContacts.map((item) => {
              return (
                <div
                  key={item.useruuid}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSelectContact(item)}
                  onKeyDown={(e) => handleKeyDown(e, item)}
                  onMouseDown={(e) => e.preventDefault()} // Prevents focus loss on click
                  style={{ cursor: 'pointer' }}
                >
                  <Contact
                    contact={item}
                    assignstaff={true}
                    className="hover-new-clicable-item"
                    hubRecord={hubRecord}
                    linkAccountChecked={linkAccountChecked}
                  />
                </div>
              );
            })}

          {selectedStaff && selectedStaff.useruuid && (
            <div key={selectedStaff.useruuid}>
              <Contact
                contact={selectedStaff}
                assignstaff={true}
                className="selected-staff-contact"
                hubRecord={hubRecord}
                linkAccountChecked={linkAccountChecked}
              />
            </div>
          )}
        </div>
      </div>
      {/* Only show relationship fields when linkAccountChecked is true AND selectedStaff has a user account */}
      {linkAccountChecked && staffHasUser && (
        <>
          <Divider />
          <div className="relationship-container">
            <div className="label">{trans('RELATIONSHIP')}</div>
            <div className="relationship-content-container">
              <div className="relationship-form-row">
                <span className="relationship-form-row-title generalinfo">{`${hubRecord?.firstname}'${trans('SRELATIONSHIPWITHTH')} ${selectedStaff?.firstname}`}</span>
                <Form.Item name="sourcerelationship" className="relationship-form-row-input-box">
                  <Input
                    placeholder={trans('ENTERTHERELATIONSHIP')}
                    name="sourcerelationship"
                    value={formData?.sourcerelationship || ''}
                    onChange={handleInputChange}
                  />
                </Form.Item>
              </div>
              <div className="relationship-form-row">
                <span className="relationship-form-row-title generalinfo">{`${selectedStaff?.firstname}'${trans('SRELATIONSHIPWITHTHIS')} ${hubRecord?.firstname}`}</span>
                <Form.Item name="targetrelationship" className="relationship-form-row-input-box">
                  <Input
                    placeholder={trans('ENTERTHERELATIONSHIP')}
                    name="targetrelationship"
                    value={formData?.targetrelationship || ''}
                    onChange={handleInputChange}
                  />
                </Form.Item>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

ContactSelectionForStaff.propTypes = {
  fetchingContacts: PropTypes.bool,
  selectedStaff: PropTypes.object,
  selectionContactList: PropTypes.array,
  onAddStaff: PropTypes.func.isRequired,
  setSelectedStaff: PropTypes.func.isRequired,
  hubRecord: PropTypes.object,
  setSearchNameApplied: PropTypes.func.isRequired,
  searchNameApplied: PropTypes.bool,
  setSearchNameText: PropTypes.func.isRequired,
  searchNameText: PropTypes.string,
  linkAccountChecked: PropTypes.bool,
  formData: PropTypes.object,
  handleInputChange: PropTypes.func,
};

export default ContactSelectionForStaff;
